import React from 'react'
import styled from 'styled-components'
import { DSText } from '@zoe/ds-web'
import { GifName, gifSrc } from '../../../data/gifs'
import { Quizzes } from '../../../redux/quiz/types'
import { ContextAwareText } from '../text/ContextAwareText'
import { TypewriterText } from '../text/TypewriterText'
import { ZiggieAvatar } from './ZiggieAvatar'

const BORDER = '1.5px solid var(--color-border-default)'

// Figma "Ziggie / Ask": a 108px row, so the screen doesn't jump when the text
// goes from 2 lines to 3. Longer text still grows it rather than clipping.
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: var(--grid-12);
  min-height: 108px;
  padding: var(--grid-8) 0;
  box-sizing: border-box;
`

const SLOT_WIDTH = 80
const SLOT_HEIGHT = 72
const SLOT_RADIUS = 16
// Ziggie's leaf can reach this far above the slot before it's clipped.
const HEADROOM = 12

const Slot = styled.div`
  position: relative;
  flex-shrink: 0;
  width: ${SLOT_WIDTH}px;
  height: ${SLOT_HEIGHT}px;
  border-radius: ${SLOT_RADIUS}px;
  background: var(--colour-surface-cream-bold);
`

// Figma masks the art with a rounded rectangle that starts HEADROOM above the slot.
const Clip = styled.div`
  position: absolute;
  top: -${HEADROOM}px;
  left: 0;
  width: ${SLOT_WIDTH}px;
  height: ${SLOT_HEIGHT + HEADROOM}px;
  border-radius: ${SLOT_RADIUS}px;
  overflow: hidden;
`

// Scale and offset measured against the Figma art: Ziggie's eyes are 29px wide
// and sit 27px in and 30px down from the slot's top-left, as in the design.
const Gif = styled.img`
  position: absolute;
  top: ${HEADROOM - 30.3}px;
  left: -20.7px;
  width: 121.4px;
  max-width: none;
`

const Bubble = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  padding: var(--grid-12) var(--grid-16);
  border: ${BORDER};
  border-radius: var(--radius-16);
  background: var(--colour-surface-white);

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -7px;
    width: 12px;
    height: 12px;
    margin-top: -6px;
    border-left: ${BORDER};
    border-bottom: ${BORDER};
    background: inherit;
    transform: rotate(45deg);
  }
`

// Fades the text in when it first shows and whenever it changes (a new
// playback). Driven from JS rather than a CSS animation: the browser replays a
// CSS animation when new styles are injected, e.g. the first time an option is
// selected, which made the unchanged question fade in again.
const FadeIn: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion || !ref.current?.animate) return
    ref.current.animate(
      [
        { opacity: 0, transform: 'translateY(4px)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 300, easing: 'ease-out' },
    )
  }, [text])

  return <div ref={ref}>{children}</div>
}

type Props = {
  gif: GifName
  // Supports <b> and quiz tokens. A new text fades in, or types out with typewriter.
  text: string
  quiz: Quizzes
  typewriter?: boolean
  // Shown under the row, e.g. "Select all that apply". Supports quiz tokens.
  subhead?: string
  // Ziggie commenting rather than asking (e.g. the symptom score): the small
  // quiz avatar and 16px regular text, with any <b> lead-in in semibold.
  compact?: boolean
}

// Ziggie asking a question, or reacting, from a speech bubble.
export const ZiggieAsk: React.FC<Props> = ({ gif, text, quiz, typewriter, subhead, compact }) => (
  <>
    <Row>
      {compact ? (
        <ZiggieAvatar gif={gif} />
      ) : (
        <Slot>
          <Clip>
            <Gif src={gifSrc(gif)} alt="" />
          </Clip>
        </Slot>
      )}
      <Bubble aria-live="polite">
        <DSText
          variant={compact ? 'fluid-paragraph-200' : 'fluid-paragraph-300'}
          weight={compact ? 'regular' : 'semiBold'}
          as="div"
        >
          {typewriter ? (
            <TypewriterText key={text} text={text} quiz={quiz} />
          ) : (
            <FadeIn text={text}>
              <ContextAwareText text={text} quiz={quiz} />
            </FadeIn>
          )}
        </DSText>
      </Bubble>
    </Row>
    {subhead && (
      <DSText variant="fluid-paragraph-300" color="secondary">
        <ContextAwareText text={subhead} quiz={quiz} />
      </DSText>
    )}
  </>
)
